<template>
  <CSidebar
    class="aw-overlay-sidebar"
    aside
    :show="isVisible"
    @update:show="(val) => !val && $store.commit('set', ['shoppingCartSidebar', false])"
    colorScheme="light"
    overlaid
    size="xl"
  >
    <div ref="cartPrintArea" class="cart-sidebar-content">
      <!-- Header -->
      <div class="cart-header">
        <div class="d-flex align-items-center justify-content-between">
          <div class="d-flex align-items-center">
            <CIcon name="cil-cart" size="xl" class="text-primary"/>
            <h4 class="mb-0 mx-2">{{ $t('order.cart.shopping_cart') }}</h4>
            <CBadge color="primary">{{ cartItemCount }}</CBadge>
          </div>
          <div class="d-flex align-items-center">
            <CSidebarClose class="asidebar-close asidebar-close--ghost" @click.native="$store.commit('set', ['shoppingCartSidebar', false])" />
          </div>
        </div>
        <!-- Last updated by info -->
        <div v-if="cartLastUpdatedBy" class="cart-last-updated mt-2">
          <small class="text-muted">
            <CIcon name="cil-user" size="sm" class="mr-1" />
            {{ $t('order.cart.last_updated_by') }}: <strong>{{ cartLastUpdatedBy.name }}</strong>
          </small>
        </div>
      </div>

      <div class="scrollable">
      <!-- Empty Cart State -->
      <div v-if="cartItemCount === 0" class="empty-cart-state">
        <CIcon name="cil-cart" size="4xl" class="text-muted mb-3"/>
        <h5 class="text-muted mb-4">{{ $t('order.cart.empty_cart') }}</h5>
        <div class="empty-cart-actions">
          <CButton
            color="success"
            variant="outline"
            class="empty-cart-action-btn"
            :disabled="loading || bulkLoading"
            @click="addAllNeedsToCart"
          >
            <CSpinner v-if="bulkLoading" size="sm" class="mr-2" />
            <CIcon v-else name="cil-cart" class="mr-2"/>
            {{ $t('warehouse.add_all_needs_to_cart') }}
          </CButton>
          <CButton
            color="primary"
            variant="ghost"
            class="empty-cart-action-btn"
            @click="navigateToNewOrder"
          >
            <CIcon name="cil-plus" class="mr-2"/>
            {{ $t('order.cart.start_new_order') }}
          </CButton>
        </div>
      </div>

      <!-- Cart Items by Supplier -->
      <div v-else class="cart-content">
        <div class="cart-search">
          <CIcon name="cil-search" class="cart-search__icon" />
          <CInput
            v-model="searchQuery"
            type="search"
            :placeholder="$t('global.search_product')"
            class="cart-search__input"
          />
        </div>

        <div v-if="filteredSupplierGroupCount === 0" class="cart-search-empty">
          <CIcon name="cil-search" size="3xl" class="text-muted mb-3" />
          <p class="mb-0 text-muted">{{ $t('global.no_filtering_results') }}</p>
        </div>

        <div v-for="(supplierGroup, supplierId) in filteredCartItemsBySupplierAndDepartment" :key="supplierId" class="supplier-group">
          <!-- Supplier Header -->
          <div class="supplier-header">
            <div class="supplier-identity">
              <div class="supplier-logo">
                <img v-if="supplierGroup.supplier.logo_url" :src="supplierGroup.supplier.logo_url" class="supplier-image"/>
                <CIcon v-else name="cil-building" size="lg" class="text-muted"/>
              </div>
              <div class="supplier-info">
                <h6 class="mb-0 supplier-title">{{ supplierGroup.supplier.name }}</h6>
                <small class="text-muted supplier-products-meta">{{ Object.values(supplierGroup.departments).reduce((total, dept) => total + dept.items.length, 0) }} {{ $t('order.cart.products') }}</small>
                <div v-if="getMinimumOrderAmount(supplierId)" class="supplier-min-order-info">
                  <small class="text-muted supplier-min-order-text">
                    {{ $t('order.price_optimizer.min_order') }}:
                    <strong>{{ formatPrice({ amount: getMinimumOrderAmount(supplierId), currency: supplierGroup.total.currency }) }}</strong>
                  </small>
                </div>
              </div>
            </div>
            <div class="supplier-actions">
              <small class="text-muted mr-2">{{ formatPrice(supplierGroup.total) }}</small>
              <CButton
                size="sm"
                color="danger"
                variant="ghost"
                @click="clearSupplierCart(supplierId)"
                v-c-tooltip="$t('order.cart.clear_supplier_items')"
              >
                <CIcon name="cil-trash"/>
              </CButton>
            </div>
          </div>

          <!-- Minimum Order Warning -->
          <div v-if="!meetsMinimumOrder(supplierId)" class="supplier-min-order-warning">
            <CAlert
              color="danger"
              class="mb-0 py-2"
            >
              <small>
                {{ $t('order.price_optimizer.min_order_warning', {
                  amount: formatPrice({
                    amount: getMinimumOrderAmount(supplierId) - supplierGroup.total.amount,
                    currency: supplierGroup.total.currency
                  })
                }) }}
              </small>
            </CAlert>
          </div>

          <!-- Department Groups -->
          <div class="supplier-items">
            <div v-for="(departmentGroup, deptKey) in supplierGroup.departments" :key="deptKey" class="department-group">
              <!-- Department Header -->
              <div class="department-header">
                <div class="d-flex align-items-center">
                  <CIcon
                    :name="`cil-${{1: 'burger', 2: 'coffee', 3: 'puzzle', null: 'minus'}[departmentGroup.departmentId]}`"
                    class="mr-2 department-icon"
                  />
                  <span class="department-name">
                    {{ getDepartmentName(departmentGroup.departmentId) }}
                  </span>
                  <small class="text-muted ml-2">({{ departmentGroup.items.length }} {{ $t('order.cart.products') }})</small>
                </div>
                <small class="text-muted">{{ formatPrice(departmentGroup.total) }}</small>
              </div>

              <!-- Department Items -->
              <div class="department-items">
                <div v-for="item in departmentGroup.items" :key="item.id" class="cart-item">
                  <div class="cart-item-row">
                    <div class="product-thumb cart-item-thumb">
                      <img
                        :src="getCartItemImage(item)"
                        :alt="item.product.name"
                        @error="onCartItemImageError"
                      />
                    </div>

                    <div class="product-main cart-item-main">
                      <div class="cart-item-top-row">
                        <div class="cart-item-top-meta">
                          <small v-if="getCartItemExternalId(item)" class="external-id">
                            #{{ getCartItemExternalId(item) }}
                          </small>
                          <CBadge
                            v-if="item.isCustom"
                            color="warning"
                            class="cart-item-custom-badge"
                          >
                            {{ $t('order.cart.custom_product_badge') }}
                          </CBadge>
                        </div>

                        <div class="cart-item-top-actions">
                          <button
                            type="button"
                            class="cart-item-remove-btn"
                            @click="removeItem(item.id)"
                          >
                            <CIcon name="cil-x" />
                          </button>
                        </div>
                      </div>

                      <div class="cart-item-middle-row">
                        <div class="cart-item-info-stack">
                          <div class="cart-item-name" :title="item.product.name">
                            {{ item.product.name }}
                          </div>
                          <div class="cart-item-price-meta">
                            <span class="product-price">{{ formatPrice(item.price) }}</span>
                            <span
                              v-if="hasCartItemDeposit(item)"
                              class="deposit-indicator"
                              role="img"
                              :aria-label="$t('returnable_packaging.deposit_order_tooltip')"
                              v-c-tooltip="$t('returnable_packaging.deposit_order_tooltip')"
                            >
                              <CIcon name="cil-recycle" />
                            </span>
                            <small v-if="item.lastModifiedBy" class="cart-item-modified-meta">
                              <CIcon name="cil-pencil" size="sm" />
                              <span>{{ item.lastModifiedBy.name }}</span>
                            </small>
                          </div>
                        </div>

                        <div class="product-cart-action cart-item-action">
                          <div
                            class="inline-order-stepper"
                            :class="{ 'has-value': item.quantity > 0 }"
                          >
                            <button
                              type="button"
                              class="inline-order-btn"
                              @click="updateQuantity(item.id, item.quantity - 1)"
                              :disabled="item.quantity <= 1"
                            >
                              <CIcon name="cil-minus" size="sm" />
                            </button>

                            <div class="inline-order-field">
                              <span class="inline-order-value">{{ item.quantity }}</span>
                            </div>

                            <button
                              type="button"
                              class="inline-order-btn inline-order-btn--plus"
                              @click="updateQuantity(item.id, item.quantity + 1)"
                            >
                              <CIcon name="cil-plus" size="sm" />
                            </button>
                          </div>
                        </div>
                      </div>

                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Custom Product Add Form -->
          <div class="custom-product-section">
            <button
              type="button"
              class="btn-add-custom-product"
              @click="toggleCustomProductForm(supplierId)"
            >
              <CIcon name="cil-plus" class="mr-1" />
              {{ $t('order.cart.add_custom_product') }}
            </button>

            <transition name="slide-down">
              <div v-if="customProductForms[supplierId] && customProductForms[supplierId].show" class="custom-product-form">
                <div class="form-group mb-2">
                  <input
                    type="text"
                    class="form-control form-control-sm"
                    :placeholder="$t('order.cart.custom_product_name_placeholder')"
                    v-model="customProductForms[supplierId].name"
                    @keyup.enter="submitCustomProduct(supplierId, supplierGroup.supplier)"
                  />
                  <small v-if="customProductForms[supplierId].nameError" class="text-danger">{{ $t('order.cart.custom_product_name_required') }}</small>
                </div>
                <div class="d-flex flex-gap-2 mb-2">
                  <div class="flex-fill">
                    <input
                      type="number"
                      class="form-control form-control-sm"
                      :placeholder="$t('order.cart.custom_product_price')"
                      v-model.number="customProductForms[supplierId].price"
                      min="0"
                      step="0.01"
                    />
                  </div>
                  <div style="width: 80px;">
                    <input
                      type="number"
                      class="form-control form-control-sm"
                      :placeholder="$t('order.cart.custom_product_qty')"
                      v-model.number="customProductForms[supplierId].quantity"
                      min="1"
                      step="1"
                    />
                  </div>
                </div>
                <div class="d-flex flex-gap-2">
                  <CButton size="sm" color="primary" class="flex-fill" @click="submitCustomProduct(supplierId, supplierGroup.supplier)">
                    <CIcon name="cil-check" class="mr-1" /> {{ $t('global.add') }}
                  </CButton>
                  <CButton size="sm" color="secondary" variant="ghost" @click="toggleCustomProductForm(supplierId)">
                    {{ $t('global.cancel') }}
                  </CButton>
                </div>
              </div>
            </transition>
          </div>

          <!-- Supplier Notes -->
          <div class="supplier-notes-section">
            <label class="notes-label">
              <CIcon name="cil-note-add" />
              {{ $t('order.cart.order_notes') }}
              <small class="text-muted">({{ $t('order.cart.order_notes_sub') }})</small>
            </label>

            <textarea
              class="form-control notes-textarea"
              :placeholder="$t('order.cart.order_notes_placeholder')"
              :value="getSupplierNotes(supplierId)"
              @input="updateSupplierNotes(supplierId, $event.target.value)"
              rows="6"
            ></textarea>
          </div>

          <!-- Supplier Order Button -->
          <div class="supplier-order-section">
            <CButton
              color="primary"
              class="w-100"
              @click="goToOrderReview"
            >
              <CIcon name="cil-check-circle"/> {{ $t('order.cart.review_and_approve') }}
            </CButton>
          </div>
        </div>
      </div>

        <mc-spinner :opacity="0.8" v-show="loading" center />
      </div>
    </div>

    <!-- Sticky Bottom Actions -->
    <div class="sticky-bottom-actions" v-if="cartItemCount > 0">
      <div class="d-flex align-items-center flex-gap-2 mb-2">

        <!-- Add Products Button - Left -->
        <CButton
          color="primary"
          variant="outline"
          size="sm"
          @click="navigateToNewOrder"
        >
          <CIcon name="cil-plus"/>
          <span class="ml-1">{{ $t('order.cart.add_products') }}</span>
        </CButton>

        <!-- Total & Optimizer Group - Right -->
        <div class="d-flex flex-gap-2 align-items-center ml-auto">
          <div
            class="d-flex flex-column align-items-end px-2 rounded cart-total-display"
            @click="goToPriceOptimizer"
            style="cursor: pointer;"
          >
            <small class="text-muted mb-0">{{ $t('order.cart.total') }}</small>
            <span class="font-weight-bold text-dark">{{ formatPrice(cartTotal) }}</span>
          </div>

          <CButton
            color="info"
            variant="outline"
            size="sm"
            style="width: 36px; height: 36px; padding: 0;"
            v-c-tooltip="{ content: $t('order.price_optimizer.title'), placement: 'top' }"
            @click="goToPriceOptimizer"
          >
            <CIcon name="cil-chart-line" />
          </CButton>
        </div>
      </div>

      <div class="d-flex flex-gap-2">
        <CButton
          color="secondary"
          variant="outline"
          @click="clearEntireCart"
          class="flex-fill"
        >
          <CIcon name="cil-trash"/> {{ $t('order.cart.clear_all') }}
        </CButton>
        <CButton
          color="primary"
          @click="goToOrderReview"
          class="flex-fill"
        >
          <CIcon name="cil-check-circle"/> {{ $t('order.cart.review_and_approve') }}
        </CButton>
      </div>
    </div>
  </CSidebar>
</template>

<script>
import { mapGetters } from 'vuex'
import api from '@/api'
import warehouseNeedsCart from '@/mixins/warehouseNeedsCart'

export default {
  name: "ShoppingCartSidebar",
  mixins: [warehouseNeedsCart],

  data() {
    return {
      loading: false,
      bulkLoading: false,
      cartRefreshThresholdMs: 60 * 1000, // Prevent redundant fetches if cart was synced recently
      customProductForms: {}, // { [supplierId]: { show, name, price, quantity, nameError } }
      searchQuery: '',
    };
  },

  computed: {
    ...mapGetters('cart', ['cartItems', 'cartItemCount', 'cartTotal', 'cartItemsBySupplier', 'cartItemsBySupplierAndDepartment', 'cartLastUpdatedBy']),

    isVisible() {
      return this.$store.state.shoppingCartSidebar === true;
    },

    searchTerm() {
      return this.normalizeSearchValue(this.searchQuery);
    },

    filteredCartItemsBySupplierAndDepartment() {
      if (!this.searchTerm) {
        return this.cartItemsBySupplierAndDepartment;
      }

      return Object.entries(this.cartItemsBySupplierAndDepartment).reduce((groups, [supplierId, supplierGroup]) => {
        const supplierName = this.normalizeSearchValue(supplierGroup.supplier?.name);
        const supplierMatches = supplierName.includes(this.searchTerm);

        const departments = Object.entries(supplierGroup.departments || {}).reduce((departmentGroups, [departmentKey, departmentGroup]) => {
          const departmentName = this.normalizeSearchValue(
            this.getDepartmentName(departmentGroup.departmentId)
          );
          const departmentMatches = departmentName.includes(this.searchTerm);

          const items = supplierMatches || departmentMatches
            ? departmentGroup.items
            : departmentGroup.items.filter((item) => {
              const values = [
                item.product?.name,
                item.product?.gtin,
                item.supplier?.name,
                this.getCartItemExternalId(item),
                departmentName,
                item.quantity,
                item.price?.amount,
                this.formatPrice(item.price),
              ];

              return values.some((value) =>
                this.normalizeSearchValue(value).includes(this.searchTerm)
              );
            });

          if (items.length > 0) {
            departmentGroups[departmentKey] = {
              ...departmentGroup,
              items,
            };
          }

          return departmentGroups;
        }, {});

        if (Object.keys(departments).length > 0) {
          groups[supplierId] = {
            ...supplierGroup,
            departments,
          };
        }

        return groups;
      }, {});
    },

    filteredSupplierGroupCount() {
      return Object.keys(this.filteredCartItemsBySupplierAndDepartment).length;
    },
  },

  watch: {
    isVisible(newVal) {
      if (newVal) {
        this.initializeCart();
        return;
      }

      this.searchQuery = '';
    }
  },

  methods: {
    /**
     * App.vue ilk yüklemeyi garanti ediyor; ShoppingCartSidebar ise sadece belirlenen "tazelik" eşiği
     * aşıldığında yeniden senkronize eder.
     */
    async initializeCart() {
      if (!this.selectedFirm) return;

      // Tazelik eşiği kaldırıldı, her zaman yeniden senkronize ediliyor. (2025-12-23)
      // const { lastSyncedAt, lastSyncedRestaurantId } = this.$store.state.cart;
      // const syncedRecently =
      //   !force &&
      //   lastSyncedRestaurantId === this.selectedFirm.id &&
      //   lastSyncedAt &&
      //   Date.now() - lastSyncedAt < this.cartRefreshThresholdMs;

      // if (syncedRecently) {
      //   return;
      // }

      // Set current restaurant ID first
      this.$store.dispatch('cart/setCurrentRestaurantId', this.selectedFirm.id);

      // Then load the cart from backend
      try {
        await this.$store.dispatch('cart/loadCartFromBackend', this.selectedFirm.id);
      } catch (error) {
        console.error('Failed to load cart from backend:', error);
      }
    },

    getWarehouseNeedsCartParams() {
      return {
        orderBy: "product",
        asc: true,
      };
    },

    updateQuantity(itemId, newQuantity) {
      this.$store.commit('cart/updateCartItemQuantity', { itemId, quantity: newQuantity });
    },

    removeItem(itemId) {
      this.$store.commit('cart/removeFromCart', itemId);
    },

    clearSupplierCart(supplierId) {
      this.$store.commit('cart/clearSupplierCart', supplierId);
    },

    clearEntireCart() {
      this.$store.commit('cart/clearCart');
    },

    getSupplierNotes(supplierId) {
      return this.$store.getters['cart/getSupplierNotes'](supplierId);
    },

    hasCartItemDeposit(item) {
      return Array.isArray(item?.orderUnit?.deposit_components) && item.orderUnit.deposit_components.length > 0
        || Array.isArray(item?.depositEstimate?.components) && item.depositEstimate.components.length > 0;
    },

    updateSupplierNotes(supplierId, notes) {
      this.$store.commit('cart/setSupplierNotes', { supplierId, notes });
    },

    async createOrder(supplierId) {
      const supplierGroup = this.cartItemsBySupplier[supplierId];

      if (!supplierGroup || !supplierGroup.items.length) {
        // this.$toast.error('No items found for this supplier');
        return;
      }

      // Check minimum order amount
      if (!this.meetsMinimumOrder(supplierId)) {
        const minimumOrder = this.getMinimumOrderAmount(supplierId);
        const remaining = minimumOrder - supplierGroup.total.amount;
        this.$toast.error(
          this.$t('order.price_optimizer.min_order_warning', {
            amount: this.formatPrice({
              amount: remaining,
              currency: supplierGroup.total.currency
            })
          })
        );
        return;
      }

      this.loading = true;

      try {
        // Prepare items for API
        const items = supplierGroup.items.map(item => ({
          product_id: item.supplierProductId, // item.product.id,
          department_id: item.product.department_id,
          quantity: item.quantity,
          price: item.price.amount,
          currency: item.price.currency,
          reason: item.reason,
        }));

        const response = await this.$axios.post(
          api.restaurants.supplierOrders.create.single(this.selectedFirm.id, supplierId),
          {
            items,
            notes: this.getSupplierNotes(supplierId)
          }
        );

        if (response.data.success) {
          this.$toast.success(
            this.$t('order.order_created_successfully', {
              supplier: supplierGroup.supplier.name
            })
          );

          // Clear supplier cart after successful order
          this.clearSupplierCart(supplierId);
        }

      } catch (error) {
        this.$toast.error(
          error.response?.data?.message ||
          this.$t('order.failed_to_create_order')
        );
      } finally {
        this.loading = false;
      }
    },

    async createAllOrders() {
      if (this.cartItemCount === 0) {
        // this.$toast.warning('Cart is empty');
        return;
      }

      // Check minimum order amounts for all suppliers
      const suppliersBelowMinimum = [];
      Object.entries(this.cartItemsBySupplier).forEach(([supplierId, supplierGroup]) => {
        if (!this.meetsMinimumOrder(supplierId)) {
          suppliersBelowMinimum.push({
            name: supplierGroup.supplier.name,
            remaining: this.getMinimumOrderAmount(supplierId) - supplierGroup.total.amount,
            currency: supplierGroup.total.currency
          });
        }
      });

      if (suppliersBelowMinimum.length > 0) {
        const warningMessage = suppliersBelowMinimum.map(s =>
          `${s.name}: ${this.formatPrice({ amount: s.remaining, currency: s.currency })}`
        ).join(', ');
        this.$toast.error(
          this.$t('order.price_optimizer.min_order_warning', {
            amount: warningMessage
          }) || `Minimum sipariş tutarına ulaşılmadı: ${warningMessage}`
        );
        return;
      }

      this.loading = true;

      try {
        // Prepare data for all suppliers
        const suppliers = Object.entries(this.cartItemsBySupplier).map(([supplierId, supplierGroup]) => ({
          supplier_id: parseInt(supplierId),
          items: supplierGroup.items.map(item => ({
            product_id: item.supplierProductId, // item.product.id,
            department_id: item.product.department_id,
            quantity: item.quantity,
            price: item.price.amount,
            currency: item.price.currency,
            reason: item.reason,
          })),
          notes: this.getSupplierNotes(supplierId)
        }));

        const response = await this.$axios.post(
          api.restaurants.supplierOrders.create.all(this.selectedFirm.id),
          { suppliers }
        );

        const { success_count, error_count, created_orders } = response.data.data;

        if (error_count === 0) {
          this.$toast.success(
            this.$t('order.all_orders_created_successfully', { count: success_count })
          );
          // Clear entire cart after all orders successful
          this.clearEntireCart();
        } else {
          this.$toast.warning(
            this.$t('order.orders_created_with_errors', {
              success: success_count,
              failed: error_count
            })
          );

          // Remove only successful orders from cart
          created_orders.forEach(order => {
            this.clearSupplierCart(order.supplier_id);
          });
        }
      } catch (error) {
        this.$toast.error(
          error.response?.data?.message ||
          this.$t('order.failed_to_create_orders')
        );
      } finally {
        this.loading = false;
      }
    },

    formatPrice(price) {
      if (!price || !price.amount) return '-';
      return this.$options.filters.toCurrency(price.amount, price.currency);
    },

    normalizeSearchValue(value) {
      return String(value || '').trim().toLocaleLowerCase();
    },

    getCartItemImage(item) {
      return item?.product?.image_url || '/img/no-product-image.png';
    },

    onCartItemImageError(event) {
      if (event?.target) {
        event.target.src = '/img/no-product-image.png';
      }
    },

    getCartItemExternalId(item) {
      return (
        item?.supplierExternalProductId ||
        item?.supplier?.supplier_external_product_id ||
        null
      );
    },

    getProductCartItems(product) {
      if (!product) {
        return [];
      }

      return this.cartItems.filter(
        (item) => Number(item.product?.id) === Number(product.id)
      );
    },

    getMinimumOrderAmount(supplierId) {
      const supplierGroup = this.cartItemsBySupplier[supplierId];
      if (!supplierGroup || !supplierGroup.supplier) {
        return null;
      }
      return parseFloat(supplierGroup.supplier.minimum_order_amount) || null;
    },

    meetsMinimumOrder(supplierId) {
      const supplierGroup = this.cartItemsBySupplier[supplierId];
      if (!supplierGroup) {
        return false;
      }
      const minimumOrder = this.getMinimumOrderAmount(supplierId);
      if (minimumOrder === null || minimumOrder === undefined) {
        return true; // If no minimum order is set, allow order
      }
      return supplierGroup.total.amount >= minimumOrder;
    },

    navigateToNewOrder() {
      // close sidebar
      this.$store.commit('set', ['shoppingCartSidebar', false]);

      // Eğer zaten NewSupplierOrder rotasındaysak navigation yapma
      if (this.$route.name === 'NewSupplierOrder') {
        return;
      }

      // Aksi halde navigation yap
      this.$router.push({ name: 'NewSupplierOrder' }).catch(err => {
        // Redundant navigation hatasını yakala ve görmezden gel
        if (err.name !== 'NavigationDuplicated') {
          console.error('Navigation error:', err);
        }
      });
    },
    goToPriceOptimizer() {
      // close sidebar
      this.$store.commit('set', ['shoppingCartSidebar', false]);

      // Eğer zaten PriceOptimizer rotasındaysak navigation yapma
      if (this.$route.name === 'PriceOptimizer') {
        return;
      }

      // Aksi halde navigation yap
      this.$router.push({ name: 'PriceOptimizer' }).catch(err => {
        // Redundant navigation hatasını yakala ve görmezden gel
        if (err.name !== 'NavigationDuplicated') {
          console.error('Navigation error:', err);
        }
      });
    },

    goToOrderReview() {
      this.$store.commit('set', ['shoppingCartSidebar', false]);

      if (this.$route.name === 'SupplierOrderReview') {
        return;
      }

      this.$router.push({ name: 'SupplierOrderReview' }).catch(err => {
        if (err.name !== 'NavigationDuplicated') {
          console.error('Navigation error:', err);
        }
      });
    },

    getDepartmentName(departmentId) {
      if (!departmentId) return this.$t('global.no_department');

      const departmentOptions = this.$globalVar.orderDepartmentOptions;
      const department = departmentOptions.find(dept => dept.value === departmentId);
      return department ? department.label : this.$t('global.no_department');
    },

    toggleCustomProductForm(supplierId) {
      if (!this.customProductForms[supplierId]) {
        this.$set(this.customProductForms, supplierId, {
          show: true,
          name: '',
          price: '',
          quantity: 1,
          nameError: false,
        });
      } else {
        this.$set(this.customProductForms[supplierId], 'show', !this.customProductForms[supplierId].show);
        if (!this.customProductForms[supplierId].show) {
          // Reset form when closing
          this.$set(this.customProductForms[supplierId], 'name', '');
          this.$set(this.customProductForms[supplierId], 'price', '');
          this.$set(this.customProductForms[supplierId], 'quantity', 1);
          this.$set(this.customProductForms[supplierId], 'nameError', false);
        }
      }
    },

    submitCustomProduct(supplierId, supplier) {
      const form = this.customProductForms[supplierId];
      if (!form) return;

      const trimmedName = (form.name || '').trim();
      if (!trimmedName) {
        this.$set(this.customProductForms[supplierId], 'nameError', true);
        return;
      }
      this.$set(this.customProductForms[supplierId], 'nameError', false);

      const price = parseFloat(form.price) || 0;
      const quantity = parseInt(form.quantity, 10) || 1;
      const currency =
        this.cartItemsBySupplier[supplierId]?.total?.currency ||
        this.$store.state.cart.cart.find(i => i.supplierId == supplierId)?.price?.currency ||
        'EUR';

      const cartItem = {
        id: `custom-${supplierId}-${Date.now()}`,
        isCustom: true,
        product: {
          id: null,
          name: trimmedName,
          gtin: null,
          image_url: null,
          department_id: null,
          packaged_qty: null,
        },
        supplierId: parseInt(supplierId, 10),
        supplierProductId: null,
        supplier: supplier,
        price: { amount: price, currency },
        quantity,
        reason: '',
        stockSnapshot: null,
        productSuppliers: [],
      };

      this.$store.commit('cart/addToCart', cartItem);

      // Reset and close form
      this.$set(this.customProductForms[supplierId], 'name', '');
      this.$set(this.customProductForms[supplierId], 'price', '');
      this.$set(this.customProductForms[supplierId], 'quantity', 1);
      this.$set(this.customProductForms[supplierId], 'show', false);
    },

  }
};
</script>


<style scoped>
.scrollable {
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  padding-bottom: 1rem;
}

.cart-sidebar-content {
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  min-height: 0;
}

/* Cart Header */
.cart-header {
  position: sticky;
  top: 0;
  z-index: 100;
  padding: 1.5rem;
  border-bottom: 1px solid #e9ecef;
  background-color: #f8f9fa;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);

  .badge {
    font-size: 0.875rem;
  }
}

.cart-last-updated {
  padding-top: 0.5rem;
  border-top: 1px solid #e9ecef;
}

/* Sidebar close button positioning */
.cart-header .asidebar-close {
  right: 1rem !important;
}

/* Empty Cart State */
.empty-cart-state {
  text-align: center;
  padding: 3rem 1.5rem;
}

.empty-cart-actions {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
  width: 100%;
  max-width: 280px;
  margin: 0 auto;
}

.empty-cart-action-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 38px;
  width: 100%;
}

/* Cart Content */
.cart-content {
  padding: 1rem;
}

.cart-search {
  position: sticky;
  top: 0;
  z-index: 4;
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  align-items: center;
  gap: 0.55rem;
  margin: -1rem -1rem 0.9rem;
  padding: 0.7rem 1rem;
  border-bottom: 1px solid #eef2f7;
  background: linear-gradient(180deg, #ffffff 0%, #ffffff 78%, rgba(255, 255, 255, 0.97) 100%);
  box-shadow: 0 10px 20px -22px rgba(15, 23, 42, 0.45);
}

.cart-search__icon {
  color: #6b7280;
}

.cart-search__input {
  margin-bottom: 0;
}

.cart-search__input ::v-deep input[type="search"] {
  border-radius: 0.65rem;
  border-color: #d8e1eb;
  background: #fbfcfe;
  font-size: 0.92rem;
}

.cart-search-empty {
  min-height: 180px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  text-align: center;
}

/* Supplier Group */
.supplier-group {
  margin-bottom: 2rem;
  border: 1px solid #e9ecef;
  border-radius: 12px;
  overflow: hidden;
  background-color: white;
  box-shadow: 0 2px 4px rgba(0,0,0,0.05);
}

.supplier-header {
  padding: 1rem;
  background-color: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.75rem;
}

.supplier-identity {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  align-items: flex-start;
  gap: 0.5rem;
  min-width: 0;
}

.supplier-min-order-info {
  margin-top: 0.25rem;
}

.supplier-min-order-warning {
  padding: 0;
  background-color: #fff5f5;
  border-bottom: 1px solid #e9ecef;
}

.supplier-logo {
  width: 2.18rem;
  height: 2.18rem;
  border: 1px solid #dbe3ee;
  border-radius: 0.35rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  object-fit: cover;
  background: #fff;
  color: #5c6873;
  flex: 0 0 auto;
}

.supplier-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: inherit;
}

.supplier-info {
  flex-grow: 1;
  min-width: 0;
}

.supplier-title {
  display: -webkit-box;
  overflow: hidden;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  word-break: break-word;
  font-size: 0.85rem;
  font-weight: 600;
  line-height: 1.28;
  color: #243041;
}

.supplier-products-meta {
  display: inline-block;
  margin-top: 0.1rem;
  font-size: 0.73rem;
  font-weight: 500;
}

.supplier-actions {
  display: flex;
  align-items: center;
  flex: 0 0 auto;
}

.supplier-actions .text-muted {
  font-size: 0.75rem;
  font-weight: 500;
}

.supplier-min-order-text {
  font-size: 0.71rem;
  font-weight: 400;
}

.supplier-min-order-text strong {
  font-weight: 600;
}

/* Supplier Items */
.supplier-items {
  padding: 0;
}

/* Department Group */
.department-group {
  margin-bottom: 1rem;
}

.department-group:last-child {
  margin-bottom: 0;
}

.department-header {
  padding: 0.75rem 1rem;
  background-color: #f8f9fa;
  border-bottom: 1px solid #e9ecef;
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 0.5rem;
}

.department-icon {
  color: #6c757d;
  font-size: 1rem;
}

.department-name {
  font-weight: 600;
  color: #2f353a;
  font-size: 0.9rem;
}

.department-items {
  padding: 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.cart-item {
  border: 1px solid #dde3ea;
  border-radius: 9px;
  background-color: #fff;
  transition: border-color 0.18s ease, box-shadow 0.18s ease;
  overflow: hidden;
}

.cart-item:hover {
  border-color: #aab7c4;
  box-shadow: 0 4px 14px rgba(35, 52, 70, 0.1);
}

.cart-item-row {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr);
  align-items: center;
  gap: 0.5rem;
  padding: 0.4rem 0.5rem;
  min-height: 56px;
}

.product-thumb {
  flex-shrink: 0;
  width: 42px;
  height: 42px;
  border-radius: 7px;
  overflow: hidden;
  background: #f3f6f9;
  border: 1px solid #edf1f5;
  display: flex;
  align-items: center;
  justify-content: center;
}

.product-thumb img {
  width: 100%;
  height: 100%;
  object-fit: contain;
}

.product-main {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.16rem;
}

.external-id {
  color: #5c6873;
  font-weight: 600;
  font-size: 0.66rem;
  line-height: 1.2;
}

.cart-item-top-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.4rem;
  min-width: 0;
}

.cart-item-top-actions {
  display: inline-flex;
  align-items: center;
  gap: 0;
  min-width: 0;
  flex-shrink: 0;
}

.cart-item-top-meta {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  flex-wrap: wrap;
  min-width: 0;
}

.cart-item-name {
  min-width: 0;
  font-size: 0.8rem;
  font-weight: 600;
  color: #1d2731;
  line-height: 1.22;
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  word-break: break-word;
}

.cart-item-custom-badge {
  font-size: 0.56rem;
  line-height: 1;
  align-self: flex-start;
  flex-shrink: 0;
}

.cart-item-middle-row {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  align-items: center;
  gap: 0.4rem;
  min-width: 0;
}

.cart-item-info-stack {
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
}

.product-price {
  font-size: 0.76rem;
  font-weight: 700;
  color: #321fdb;
  line-height: 1;
  white-space: nowrap;
}

.cart-item-price-meta {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  min-width: 0;
  flex-wrap: wrap;
}

.deposit-indicator {
  width: 1.1rem;
  height: 1.1rem;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: #e5f7ec;
  color: #198754;
  font-size: 0.68rem;
  line-height: 1;
  flex: 0 0 auto;
}

.cart-item-modified-meta {
  display: inline-flex;
  align-items: center;
  gap: 0.16rem;
  min-width: 0;
  max-width: 100%;
  color: #7b8794;
  font-size: 0.61rem;
  font-weight: 600;
  line-height: 1;
}

.cart-item-modified-meta span {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.product-cart-action {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-self: end;
}

.inline-order-stepper {
  display: inline-flex;
  align-items: center;
  gap: 0.1rem;
  width: fit-content;
  padding: 0.12rem;
  border-radius: 999px;
  border: 1px solid #d9e2ec;
  background: linear-gradient(180deg, #ffffff 0%, #f7fafc 100%);
  box-shadow: 0 14px 24px -22px rgba(15, 23, 42, 0.9);
  transition: border-color 0.18s ease, box-shadow 0.18s ease, transform 0.18s ease;
}

.inline-order-stepper:hover,
.inline-order-stepper:focus-within {
  border-color: #9fb3c8;
  box-shadow: 0 16px 28px -22px rgba(15, 23, 42, 0.85);
}

.inline-order-stepper.has-value {
  border-color: rgba(46, 184, 92, 0.45);
  box-shadow: 0 18px 30px -24px rgba(46, 184, 92, 0.6);
}

.inline-order-btn {
  width: 24px;
  height: 24px;
  border: none;
  border-radius: 50%;
  background: #edf2f7;
  color: #405261;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  cursor: pointer;
  transition: transform 0.15s ease, background-color 0.15s ease, color 0.15s ease;
}

.inline-order-btn:focus-visible,
.cart-item-remove-btn:focus-visible {
  outline: 2px solid rgba(50, 31, 219, 0.24);
  outline-offset: 1px;
}

.inline-order-btn:hover:not(:disabled) {
  background: #e2e8f0;
  color: #1d2731;
  transform: translateY(-1px);
}

.inline-order-btn:disabled {
  cursor: not-allowed;
  opacity: 0.45;
  transform: none;
}

.inline-order-btn--plus {
  background: linear-gradient(180deg, #d9fbe5 0%, #b5f0c8 100%);
  color: #17653d;
}

.inline-order-btn--plus:hover:not(:disabled) {
  background: linear-gradient(180deg, #c9f6d8 0%, #9ce6b6 100%);
  color: #11522f;
}

.inline-order-field {
  width: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.inline-order-value {
  width: 100%;
  text-align: center;
  font-size: 0.76rem;
  font-weight: 700;
  color: #1d2731;
  line-height: 1.1;
}

.cart-item-remove-btn {
  width: 22px;
  height: 22px;
  border: none;
  border-radius: 50%;
  background: #fff1f2;
  color: #cc3344;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  cursor: pointer;
  transition: transform 0.15s ease, background-color 0.15s ease, color 0.15s ease;
  flex-shrink: 0;
}

.cart-item-remove-btn .c-icon {
  width: 11px;
  height: 11px;
}

.cart-item-remove-btn:hover {
  background: #ffe3e6;
  color: #b42318;
  transform: translateY(-1px);
}

/* Custom Product Section */
.custom-product-section {
  padding: 0.65rem 1rem 0;
  background-color: #fff;
  border-top: 1px solid #e9ecef;
}

.btn-add-custom-product {
  display: inline-flex;
  align-items: center;
  font-size: 0.8rem;
  font-weight: 600;
  color: #768392;
  background: none;
  border: 1px dashed #bcc5ce;
  border-radius: 6px;
  padding: 0.3rem 0.65rem;
  cursor: pointer;
  transition: color 0.15s, border-color 0.15s, background 0.15s;
  width: 100%;
  justify-content: center;
  margin-bottom: 0.5rem;
}

.btn-add-custom-product:hover {
  color: #321fdb;
  border-color: #321fdb;
  background: #f0f0ff;
}

.custom-product-form {
  background: #f8f9fc;
  border: 1px solid #dde3ea;
  border-radius: 8px;
  padding: 0.65rem;
  margin-bottom: 0.65rem;
}

/* Slide-down transition */
.slide-down-enter-active,
.slide-down-leave-active {
  transition: max-height 0.2s ease, opacity 0.2s ease;
  max-height: 300px;
  overflow: hidden;
}

.slide-down-enter,
.slide-down-leave-to {
  max-height: 0;
  opacity: 0;
}

/* Supplier Notes Section */
.supplier-notes-section {
  padding: 1rem;
  background-color: #fff;
  border-top: 1px solid #e9ecef;
}

.notes-label {
  font-size: 0.875rem;
  font-weight: 600;
  color: #495057;
  margin-bottom: 0.5rem;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 0.175rem;
}

.notes-textarea {
  font-size: 0.875rem;
  border-radius: 6px;
  border: 1px solid #d1d5db;
  resize: vertical;
  min-height: 60px;
}

.notes-textarea:focus {
  border-color: #3c8dbc;
  box-shadow: 0 0 0 0.2rem rgba(60, 141, 188, 0.15);
}

/* Supplier Order Section */
.supplier-order-section {
  padding: 1rem;
  background-color: #f8f9fa;
  border-top: 1px solid #e9ecef;
}

.supplier-order-section .btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

/* Sticky Bottom Actions */
.sticky-bottom-actions {
  flex: 0 0 auto;
  padding: 0.5rem 1rem calc(1rem + env(safe-area-inset-bottom, 0px));
  background: linear-gradient(transparent, rgba(255, 255, 255, 0.9) 20%, white 50%);
  backdrop-filter: blur(4px);
  border-top: 1px solid #e9ecef;
}

/* Cart Total Display */
.cart-total-display {
  cursor: pointer;
  transition: background-color 0.15s ease;
}

.cart-total-display:hover {
  background-color: #f8f9fa;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .supplier-header {
    align-items: flex-start;
  }

  .supplier-identity {
    align-items: flex-start;
  }

  .department-items {
    padding: 0.5rem;
  }
}

@media (max-width: 480px) {
  .cart-item-row {
    gap: 0.45rem;
    padding: 0.38rem 0.42rem;
  }

  .cart-item-top-row {
    gap: 0.3rem;
  }

  .cart-item-price-meta {
    gap: 0.28rem;
  }

  .inline-order-stepper {
    padding: 0.14rem;
  }

  .inline-order-btn,
  .cart-item-remove-btn {
    width: 24px;
    height: 24px;
  }

  .inline-order-btn .c-icon,
  .cart-item-remove-btn .c-icon {
    width: 12px;
    height: 12px;
  }

  .inline-order-field {
    width: 24px;
  }

  .inline-order-value {
    font-size: 0.74rem;
  }

  .product-thumb {
    width: 42px;
    height: 42px;
  }

  .cart-item-name {
    font-size: 0.78rem;
  }

  .product-price {
    font-size: 0.74rem;
  }

  .cart-item-modified-meta {
    font-size: 0.58rem;
  }
}
</style>
